import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchCategories, fetchPickupCache, fetchPier6, fetchPier7, fetchPier8, fetchWineCache, searchLots, searchLiveLots } from "@/lib/api";


const NO_RESULTS_MSG = "Nessun lotto trovato con questi parametri.";

function notifyOutcome(filteredCount) {
  if (filteredCount === 0) {
    toast(NO_RESULTS_MSG, { description: "Prova ad ampliare i filtri." });
    return;
  }
  toast.success(`${filteredCount} lotti trovati.`);
}

function buildStandardLabel(payload, categories) {
  const parts = [];
  if (payload.q) parts.push(`"${payload.q}"`);
  const cat = categories.find((c) => String(c.id) === String(payload.categoryId));
  if (cat) parts.push(cat.name);
  if (parts.length === 0) parts.push("Tutti i lotti");
  return parts.join(" · ");
}

function buildLiveLabel(payload) {
  const parts = ["Asta Live"];
  if (payload.q) parts.push(`"${payload.q}"`);
  parts.push(`entro ${payload.maxMinutesRemaining}min`);
  return parts.join(" · ");
}

function passesCoglione2(lot) {
  const bid = lot.current_bid_eur || lot.starting_bid_eur || 0;
  if (!bid) return false;
  return bid * 0.09 + 3 + (lot.shipping_eur || 0) <= 15;
}

function buildMultidayLabel(payload) {
  const parts = [];
  if (payload.q) parts.push(`"${payload.q}"`);
  if (payload.maxHoursRemaining) {
    const days = payload.maxHoursRemaining / 24;
    parts.push(`entro ${days} giorn${days === 1 ? "o" : "i"}`);
  }
  return parts.join(" · ");
}



export default function useSearch() {
  const [mode, setMode] = useState("standard");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastQueryLabel, setLastQueryLabel] = useState("");
  const [lastPayload, setLastPayload] = useState(null);
  const resultsRef = useRef(null);

  const scrollToResults = useCallback(() => {
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchCategories()
      .then((cats) => { if (mounted) setCategories(cats); })
      .catch(() => toast.error("Impossibile caricare le categorie."))
      .finally(() => { if (mounted) setLoadingCategories(false); });
    return () => { mounted = false; };
  }, []);

  const runStandardSearch = useCallback(async (payload) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await searchLots({ ...payload, page: 1 });
      setResults(data);
      setLastQueryLabel(buildStandardLabel(payload, categories));
      setLastPayload(payload);
      notifyOutcome(data.filtered_count);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la ricerca.");
    } finally {
      setSearching(false);
    }
  }, [categories, scrollToResults]);

  const loadMore = useCallback(async () => {
    if (!lastPayload || !results || !results.has_more || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = (results.page || 1) + 1;
      const data = await searchLots({ ...lastPayload, page: nextPage });
      setResults((prev) => {
        if (!prev) return data;
        return {
          ...data,
          items: [...prev.items, ...data.items],
          filtered_count: prev.filtered_count + data.items.length,
          returned: prev.returned + data.returned,
        };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricando la pagina successiva.");
    } finally {
      setLoadingMore(false);
    }
  }, [lastPayload, results, loadingMore]);

  const runMultidaySearch = useCallback(async (payload) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await searchLots({ ...payload, page: 1 });
      setResults(data);
      setLastQueryLabel(buildMultidayLabel(payload));
      setLastPayload(payload);
      notifyOutcome(data.filtered_count);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la ricerca.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  const runLiveSearch = useCallback(async (payload) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    toast.info("Interrogando le aste live di Catawiki…", {
      description: "Può richiedere fino a 30 secondi.",
      duration: 4000,
    });
    try {
      const data = await searchLiveLots(payload);
      setResults(data);
      setLastQueryLabel(buildLiveLabel(payload));
      setLastPayload(null); // live mode does not support pagination
      notifyOutcome(data.filtered_count);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la ricerca live.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  const switchMode = useCallback((next) => {
    setMode((current) => {
      if (current === next) return current;
      setResults(null);
      setHasSearched(false);
      setLastQueryLabel("");
      setLastPayload(null);
      return next;
    });
  }, []);


  const runPier4Search = useCallback(async () => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await fetchPickupCache();
      setResults({
        items: data.items,
        total: data.total,
        filtered_count: data.total,
        returned: data.items.length,
        has_more: false,
        page: 1,
        cache_stats: data.cache_stats,
      });
      setLastQueryLabel("Cache pickup-only");
      setLastPayload({ pier4Mode: true });
      notifyOutcome(data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricando la cache.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  const runPier5Search = useCallback(async () => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await fetchPickupCache();
      setResults({
        items: data.items,
        total: data.items.length,
        filtered_count: data.items.length,
        returned: data.items.length,
        has_more: false,
        page: 1,
        cache_stats: data.cache_stats,
      });
      setLastQueryLabel("Cache pickup (tutti)");
      setLastPayload({ pier5Mode: true });
      notifyOutcome(data.items.length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricando la cache.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  const runPier6Search = useCallback(async ({ country = null } = {}) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await fetchPier6({ country });
      setResults({
        items: data.items,
        total: data.total,
        filtered_count: data.total,
        returned: data.items.length,
        has_more: data.has_more,
        page: data.page,
        countries: data.countries,
        cache_stats: data.cache_stats,
        pier6Mode: true,
      });
      setLastQueryLabel("Pier 6 — Pickup Only");
      setLastPayload({ pier6Mode: true, country });
      notifyOutcome(data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricando Pier 6.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  const runCoglione2Search = useCallback(async (payload) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();

    const MAX_PAGES = 10;
    const MIN_RESULTS = 12;

    try {
      let allFiltered = [];
      let page = 1;
      let hasMore = true;

      const timePayload = { ...payload, maxHoursRemaining: payload.maxHoursRemaining || 1 };

      while (page <= MAX_PAGES && allFiltered.length < MIN_RESULTS && hasMore) {
        const data = await searchLots({ ...timePayload, page, perPage: 48 });
        allFiltered = [...allFiltered, ...(data.items || []).filter(passesCoglione2)];
        hasMore = !!data.has_more;
        page++;
      }

      setResults({
        items: allFiltered,
        total: allFiltered.length,
        filtered_count: allFiltered.length,
        returned: allFiltered.length,
        has_more: hasMore,
        page: 1,
        coglione2NextPage: page,
        coglione2TimePayload: timePayload,
      });
      setLastQueryLabel(buildStandardLabel(payload, categories));
      setLastPayload(payload);
      notifyOutcome(allFiltered.length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore ricerca Coglione 2.");
    } finally {
      setSearching(false);
    }
  }, [categories, scrollToResults]);

  const loadMoreCoglione2 = useCallback(async () => {
    if (!results || !results.has_more || loadingMore) return;
    const nextPage = results.coglione2NextPage;
    const timePayload = results.coglione2TimePayload;
    if (!nextPage || !timePayload) return;
    setLoadingMore(true);
    try {
      const data = await searchLots({ ...timePayload, page: nextPage, perPage: 48 });
      const filtered = (data.items || []).filter(passesCoglione2);
      setResults((prev) => ({
        ...prev,
        items: [...(prev?.items || []), ...filtered],
        total: (prev?.total || 0) + filtered.length,
        filtered_count: (prev?.filtered_count || 0) + filtered.length,
        returned: (prev?.returned || 0) + filtered.length,
        has_more: !!data.has_more,
        coglione2NextPage: nextPage + 1,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricando altri lotti.");
    } finally {
      setLoadingMore(false);
    }
  }, [results, loadingMore]);

  const runCoglioneSearch = useCallback(async ({ q = null } = {}) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await searchLots({ maxHoursRemaining: 1, q: q || undefined, perPage: 96 });
      const pickupItems = (data.items || []).filter((i) => i.pickup_only === true);
      setResults({
        items: pickupItems,
        total: pickupItems.length,
        filtered_count: pickupItems.length,
        returned: pickupItems.length,
        has_more: false,
        page: 1,
        coglioneMode: true,
      });
      setLastQueryLabel(`Coglione${q ? ` · "${q}"` : ""}`);
      setLastPayload({ coglioneMode: true, q });
      notifyOutcome(pickupItems.length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore ricerca Coglione.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  const runPickupLiveSearch = useCallback(async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const data = await searchLiveLots({ maxMinutesRemaining: 30, maxAuctions: 50 });
      const pickupItems = (data.items || [])
        .filter((i) => !!i.pickup_city)
        .sort((a, b) => (a.seconds_remaining ?? Infinity) - (b.seconds_remaining ?? Infinity));
      setResults({
        items: pickupItems,
        total: pickupItems.length,
        filtered_count: pickupItems.length,
        returned: pickupItems.length,
        has_more: false,
        page: 1,
        pickupLiveMode: true,
        totalScanned: data.filtered_count ?? data.returned,
      });
      setLastQueryLabel("Pickup Live");
      setLastPayload({ pickupLiveMode: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore ricerca live.");
    } finally {
      setSearching(false);
    }
  }, []);

  const runPier7Search = useCallback(async ({ country = null } = {}) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await fetchPier7({ country });
      setResults({
        items: data.items,
        total: data.total,
        total_in_db: data.total_in_db,
        filtered_count: data.total,
        returned: data.items.length,
        has_more: data.has_more,
        page: data.page,
        countries: data.countries,
        cache_stats: data.cache_stats,
        pier7Mode: true,
      });
      setLastQueryLabel("Pier 7 — Pickup Only (cache aste)");
      setLastPayload({ pier7Mode: true, country });
      notifyOutcome(data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricando Pier 7.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  const runPier8Search = useCallback(async ({ country = null } = {}) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await fetchPier8({ country });
      setResults({
        items: data.items,
        total: data.total,
        total_in_db: data.total_in_db,
        filtered_count: data.total,
        returned: data.items.length,
        has_more: data.has_more,
        page: data.page,
        countries: data.countries,
        cache_stats: data.cache_stats,
        pier8Mode: true,
      });
      setLastQueryLabel("Pier 8 — Pickup Only (cache + live)");
      setLastPayload({ pier8Mode: true, country });
      notifyOutcome(data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricando Pier 8.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  const runWineSearch = useCallback(async ({ category = null, seller_country = null } = {}) => {
    setSearching(true);
    setHasSearched(true);
    scrollToResults();
    try {
      const data = await fetchWineCache({ category, seller_country });
      setResults({
        items: data.items,
        total: data.total,
        filtered_count: data.total,
        returned: data.items.length,
        has_more: data.has_more,
        page: data.page,
        categories: data.categories,
        countries: data.countries,
        cache_stats: data.cache_stats,
        wineMode: true,
      });
      setLastQueryLabel("Vini & Spirits");
      setLastPayload({ wineMode: true, category, seller_country });
      notifyOutcome(data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricando la cache vini.");
    } finally {
      setSearching(false);
    }
  }, [scrollToResults]);

  return {
    mode, switchMode,
    categories, loadingCategories,
    results, searching, loadingMore, hasSearched, lastQueryLabel, lastPayload, resultsRef,
    runStandardSearch, runMultidaySearch, runLiveSearch,
    runPier4Search, runPier5Search, runPier6Search, runPier7Search, runPier8Search, runPickupLiveSearch, runCoglioneSearch, runCoglione2Search, runWineSearch, loadMore, loadMoreCoglione2,
  };
}
